import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import type { GeofenceDetectionMethod } from "@src/appV2/Location/geofence/useGeofence";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { BottomSheetIllustrationContent } from "@src/appV2/redesign/system/ui-components";
import { useMutation } from "@tanstack/react-query";
import { differenceInSeconds } from "date-fns";
import { useEffect, useRef } from "react";

import type { TimekeepingAction } from "./constants";

export interface TooFarAwayContentProps {
  workplaceName: string;

  /**
   * Identifying context used to emit funnel analytics events. These fields
   * are forwarded to every `Too Far Away *` event so the events can be
   * correlated by shift/worker and joined to other timekeeping funnels.
   */
  action: Extract<TimekeepingAction, "clock-in" | "clock-out">;
  shiftId: string;
  workerId: string;
  workplaceId: string | undefined;
  detectionMethod: GeofenceDetectionMethod;
  currentInside: boolean;
  lastGeofenceChangeAt: Date | undefined;

  onTryAgain: () => Promise<void>;
  onSkipLocationCheck: () => void;
  onCancel: () => void;
}

const MIN_LOADER_DURATION_IN_MS = 2000;

export function TooFarAwayContent(props: TooFarAwayContentProps) {
  const {
    workplaceName,
    action,
    shiftId,
    workerId,
    workplaceId,
    detectionMethod,
    currentInside,
    lastGeofenceChangeAt,
    onTryAgain: onTryAgainProperty,
    onSkipLocationCheck: onSkipLocationCheckProperty,
    onCancel: onCancelProperty,
  } = props;

  const timerRef = useRef<NodeJS.Timeout>();
  const modalOpenedAtRef = useRef<Date>(new Date());
  const retryAttemptRef = useRef<number>(0);

  const baseEventPayload = { action, shiftId, workerId, workplaceId, detectionMethod };

  function secondsSinceModalOpen(): number {
    return differenceInSeconds(new Date(), modalOpenedAtRef.current);
  }

  function secondsSinceLastGeofenceChange(): number | undefined {
    if (!isDefined(lastGeofenceChangeAt)) {
      return undefined;
    }
    return differenceInSeconds(new Date(), lastGeofenceChangeAt);
  }

  useEffect(
    () => {
      logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_MODAL_OPENED, {
        ...baseEventPayload,
        currentInside,
        secondsSinceLastGeofenceChange: secondsSinceLastGeofenceChange(),
      });
    },
    // Fires once per mount — opening the modal is a single funnel entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { isLoading: isGeofenceCheckLoading, mutateAsync: onTryAgain } = useMutation({
    mutationFn: async () => {
      retryAttemptRef.current += 1;
      const retryAttempt = retryAttemptRef.current;

      logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_TRY_AGAIN_TAPPED, {
        ...baseEventPayload,
        retryAttempt,
        currentInside,
        secondsSinceModalOpen: secondsSinceModalOpen(),
        secondsSinceLastGeofenceChange: secondsSinceLastGeofenceChange(),
      });

      const now = Date.now();

      await onTryAgainProperty();

      // Log RESULT immediately after onTryAgainProperty resolves — before the
      // min-loader wait. The parent's onTryAgain closes the modal, which unmounts
      // this component and cancels the timer via useEffect cleanup. Logging here
      // ensures the event always fires regardless of unmount timing.
      // TODO(TEM-2695): add `resultInside` once Phase 4 wires in a real refetch.
      logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_TRY_AGAIN_RESULT, {
        ...baseEventPayload,
        retryAttempt,
        secondsSinceModalOpen: secondsSinceModalOpen(),
      });

      /**
       * We want to have a minimum loader duration so
       * HCP can see that the action was executed,
       * even when it lasted for a short time.
       */
      const timeElapsedInMs = Date.now() - now;
      if (timeElapsedInMs < MIN_LOADER_DURATION_IN_MS) {
        await new Promise((resolve) => {
          timerRef.current = setTimeout(resolve, MIN_LOADER_DURATION_IN_MS - timeElapsedInMs);
        });
      }
    },
  });

  function handleSkipLocationCheck() {
    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_SKIP_LOCATION_TAPPED, {
      ...baseEventPayload,
      retryAttempt: retryAttemptRef.current,
      secondsSinceModalOpen: secondsSinceModalOpen(),
    });
    onSkipLocationCheckProperty();
  }

  function handleCancel() {
    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_MODAL_DISMISSED, {
      ...baseEventPayload,
      retryAttempt: retryAttemptRef.current,
      secondsSinceModalOpen: secondsSinceModalOpen(),
    });
    onCancelProperty();
  }

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      <BottomSheetIllustrationContent
        illustrationType="location-error"
        title="Too far away"
        description={`It looks like you are not inside ${workplaceName}. Please try again when you are inside the facility. `}
      />
      <DialogFooter
        orientation="vertical"
        sx={{ marginTop: 4 }}
        closeButtonAriaLabel="Cancel"
        onClose={() => {
          handleCancel();
        }}
      >
        <LoadingButton
          fullWidth
          isLoading={isGeofenceCheckLoading}
          variant="contained"
          size="large"
          onClick={async () => {
            await onTryAgain();
          }}
        >
          I&apos;m inside the facility — Try again
        </LoadingButton>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => {
            handleSkipLocationCheck();
          }}
        >
          Skip location confirmation
        </Button>
      </DialogFooter>
    </>
  );
}
